import Decimal from 'decimal.js';
import { request } from '../../../shared/api/client';
import type { Paginated, Payable, Receivable } from '../api/useDebts';

export interface CurrencyBalance {
  currencyId: string;
  receivable: Decimal;
  payable: Decimal;
  balance: Decimal;
  receivables: Receivable[];
  payables: Payable[];
}

async function openDebts<T extends Receivable>(path: string, contactId: string): Promise<T[]> {
  const rows: T[] = [];
  let total = 0;
  do {
    const page = await request<Paginated<T>>(
      `${path}?contactId=${encodeURIComponent(contactId)}&status=OPEN&limit=200&offset=${rows.length}`,
    );
    rows.push(...page.data);
    total = page.total;
    if (page.data.length === 0) break;
  } while (rows.length < total);
  return rows.filter((row) => row.paymentStatus !== 'PAID');
}

export async function loadContactOpenDebts(contactId: string) {
  const [receivables, payables] = await Promise.all([
    openDebts<Receivable>('/receivables', contactId),
    openDebts<Payable>('/payables', contactId),
  ]);
  return { receivables, payables };
}

export function summarizeContactBalance(
  receivables: Receivable[],
  payables: Payable[],
): CurrencyBalance[] {
  const byCurrency = new Map<string, CurrencyBalance>();
  function row(currencyId: string) {
    let result = byCurrency.get(currencyId);
    if (!result) {
      result = {
        currencyId,
        receivable: new Decimal(0),
        payable: new Decimal(0),
        balance: new Decimal(0),
        receivables: [],
        payables: [],
      };
      byCurrency.set(currencyId, result);
    }
    return result;
  }
  for (const debt of receivables) {
    const entry = row(debt.currencyId);
    entry.receivable = entry.receivable.plus(debt.outstandingAmount);
    entry.receivables.push(debt);
  }
  for (const debt of payables) {
    const entry = row(debt.currencyId);
    entry.payable = entry.payable.plus(debt.outstandingAmount);
    entry.payables.push(debt);
  }
  for (const entry of byCurrency.values()) {
    entry.balance = entry.receivable.minus(entry.payable);
  }
  return [...byCurrency.values()].sort((a, b) => a.currencyId.localeCompare(b.currencyId));
}
